import {
  ChangeEventHandler,
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react'

import styled, { css } from 'styled-components'

import { desktopCSS, mobileCSS } from '@measures/responsive'
import translate from '@i18n'
import IconButton from '@components/Buttons/IconButton'
import SvgIcon from '@components/SvgIcon'

interface SearchbarProps {
  initialSearchTerm?: string
  onSearchTermUpdate: (searchTerm: string) => void
}

const SearchBarWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing8, spacing16, spacing80, spacing24 },
      colors: { strokeDefault, fillDefault, strokeStrong },
    },
  }) => css`
    border: 1px solid ${strokeDefault};
    padding: 0 ${spacing8};
    border-radius: ${spacing80};
    background-color: ${fillDefault};
    display: flex;
    align-items: center;
    gap: ${spacing16};
    width: calc(100% - ${spacing24});
    &:focus-visible {
      outline: 2px solid ${strokeStrong};
    }
  `}
`

const SearchInput = styled.input`
  ${({
    theme: {
      colors: { fillDefault, textDefault },
      spacing: { spacing8, spacing80 },
      typography: {
        bodycopy: {
          small2: { bundledCSS: small2BodyCopy },
        },
      },
    },
  }) => css`
    ${small2BodyCopy};
    color: ${textDefault};
    padding: ${spacing8};
    background-color: ${fillDefault};
    border-radius: ${spacing80};
    border: none;
    &:focus-visible {
      outline: none;
    }
    ${mobileCSS(css`
      max-width: 40vw;
    `)}
  `}
`

const ClearIconButton = styled(IconButton)`
  ${({
    theme: {
      colors: { iconDefault },
    },
  }) => css`
    ${desktopCSS(css`
      display: none;
    `)}
    color: ${iconDefault};
    margin-left: auto;
  `}
`

const SearchIcon = styled(SvgIcon)`
  ${({
    theme: {
      spacing: { spacing24 },
    },
  }) => css`
    min-height: ${spacing24};
    min-width: ${spacing24};
  `}
`

const Searchbar: FunctionComponent<SearchbarProps> = ({
  initialSearchTerm,
  onSearchTermUpdate,
}) => {
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm ?? '')

  const handleClearSearch = useCallback(() => {
    setSearchTerm('')
  }, [])

  const handleInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      setSearchTerm(e.target.value)
    },
    [],
  )

  useEffect(() => {
    onSearchTermUpdate(searchTerm)
  }, [onSearchTermUpdate, searchTerm])

  return (
    <SearchBarWrapper>
      <SearchIcon
        aria-label={translate('search.defaultButtonTitle')}
        iconName={'magnifying-glass'}
        size={24}
      />
      <SearchInput
        type="text"
        value={searchTerm}
        onChange={handleInputChange}
        placeholder={translate('fmtSearchbar.placeholder')}
      />
      <ClearIconButton
        ariaLabel={translate('close')}
        buttonSize={48}
        onClick={handleClearSearch}
        iconName="xmark-large"
        iconSize={24}
      />
    </SearchBarWrapper>
  )
}

export default Searchbar
